import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { resolveGuideImageUrl, resolveGuideVideoUrl } from '../../utils/guideImageUrl';
import { getDefaultGuidesHome, resolveGuidesHome } from '../../utils/vaGuidesHome';
import VAGuidesHomeEditor, { serializeGuidesHomeForSave } from './VAGuidesHomeEditor';
import {
  BookOpenIcon,
  PlusIcon,
  TrashIcon,
  PhotoIcon,
  FilmIcon,
  FolderIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';

const UNCATEGORIZED_ID = '__uncategorized__';

function emptyGuide(order = 0) {
  return {
    title: '',
    description: '',
    published: true,
    order,
    categoryId: null,
    steps: []
  };
}

function emptyCategory(order = 0) {
  return {
    _id: crypto.randomUUID(),
    title: 'New category',
    imageUrl: null,
    displayUrl: null,
    order
  };
}

function migrateLegacyPrivacyGuide(config) {
  const guides = Array.isArray(config?.guides) ? [...config.guides] : [];
  if (guides.length > 0) return guides;
  const legacySteps = config?.privacyGuide?.steps || [];
  if (legacySteps.length === 0) return [];
  return [{
    title: 'Profile privacy steps',
    description: '',
    published: true,
    order: 0,
    categoryId: null,
    steps: legacySteps
  }];
}

function guideKey(guide, idx) {
  return guide._id || `guide-${idx}`;
}

function CategoryIcon({ category }) {
  const src = resolveGuideImageUrl(category.imageUrl, category.displayUrl);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="w-10 h-10 rounded-xl object-cover border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center font-semibold text-sm">
      {(category.title || 'C').charAt(0).toUpperCase()}
    </div>
  );
}

const VAGuidesSection = () => {
  const [guides, setGuides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [overviewConfig, setOverviewConfig] = useState({
    followersPerAccount: 0,
    filterRules: { westernNames: '', description: '' }
  });
  const [savingOverview, setSavingOverview] = useState(false);
  const [guidesHome, setGuidesHome] = useState(() => getDefaultGuidesHome());
  const [savingHome, setSavingHome] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGuideKey, setExpandedGuideKey] = useState(null);
  const [draggingGuideKey, setDraggingGuideKey] = useState(null);
  const [dragOverCategoryId, setDragOverCategoryId] = useState(null);

  const loadGuides = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/va/config');
      if (res.data.success) {
        const config = res.data.config || {};
        const migrated = migrateLegacyPrivacyGuide(config);
        setGuides(migrated);
        setCategories(
          (Array.isArray(config.guideCategories) ? config.guideCategories : [])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
        setOverviewConfig({
          followersPerAccount: config.followersPerAccount || 0,
          filterRules: {
            westernNames: config.filterRules?.westernNames || '',
            description: config.filterRules?.description || ''
          }
        });
        setGuidesHome(resolveGuidesHome(config.guidesHome));
        if (migrated.length > 0) {
          setExpandedGuideKey(guideKey(migrated[0], 0));
        }
      }
    } catch (err) {
      toast.error('Failed to load guides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGuides();
  }, [loadGuides]);

  const guidesByCategory = useMemo(() => {
    const map = new Map(categories.map((c) => [String(c._id), []]));
    const uncategorized = [];
    guides.forEach((guide, idx) => {
      const entry = { guide, idx };
      const categoryId = guide.categoryId ? String(guide.categoryId) : null;
      if (categoryId && map.has(categoryId)) {
        map.get(categoryId).push(entry);
      } else {
        uncategorized.push(entry);
      }
    });
    return { map, uncategorized };
  }, [guides, categories]);

  const publishedGuides = useMemo(
    () => guides.filter((g) => g.published !== false && String(g.title || '').trim()),
    [guides]
  );

  const updateGuide = (idx, patch) => {
    setGuides((prev) => prev.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };

  const updateStep = (guideIdx, stepIdx, patch) => {
    setGuides((prev) => {
      const next = [...prev];
      const steps = [...(next[guideIdx].steps || [])];
      steps[stepIdx] = { ...steps[stepIdx], ...patch };
      next[guideIdx] = { ...next[guideIdx], steps };
      return next;
    });
  };

  const updateCategory = (categoryId, patch) => {
    setCategories((prev) => prev.map((c) => (String(c._id) === String(categoryId) ? { ...c, ...patch } : c)));
  };

  const addCategory = () => {
    setCategories((prev) => [...prev, emptyCategory(prev.length)]);
  };

  const removeCategory = (categoryId) => {
    if (!window.confirm('Delete this category? Guides inside will move to Uncategorized.')) return;
    setCategories((prev) => prev.filter((c) => String(c._id) !== String(categoryId)));
    setGuides((prev) => prev.map((g) => (
      String(g.categoryId) === String(categoryId) ? { ...g, categoryId: null } : g
    )));
  };

  const addGuide = (categoryId = null) => {
    setGuides((prev) => {
      const next = [...prev, { ...emptyGuide(prev.length), categoryId }];
      const idx = next.length - 1;
      setExpandedGuideKey(guideKey(next[idx], idx));
      return next;
    });
  };

  const removeGuide = (idx) => {
    if (!window.confirm('Delete this guide?')) return;
    const key = guideKey(guides[idx], idx);
    setGuides((prev) => prev.filter((_, i) => i !== idx));
    setExpandedGuideKey((prev) => (prev === key ? null : prev));
  };

  const assignGuideToCategory = (guideIdx, categoryId) => {
    const normalized = categoryId === UNCATEGORIZED_ID ? null : categoryId;
    updateGuide(guideIdx, { categoryId: normalized });
  };

  const handleGuideDrop = (categoryId) => {
    if (!draggingGuideKey) return;
    const guideIdx = guides.findIndex((g, idx) => guideKey(g, idx) === draggingGuideKey);
    if (guideIdx === -1) return;
    assignGuideToCategory(guideIdx, categoryId);
    setDraggingGuideKey(null);
    setDragOverCategoryId(null);
  };

  const addStep = (guideIdx) => {
    setGuides((prev) => {
      const next = [...prev];
      const steps = [...(next[guideIdx].steps || []), { title: '', body: '', imageUrl: null, videoUrl: null }];
      next[guideIdx] = { ...next[guideIdx], steps };
      return next;
    });
  };

  const removeStep = (guideIdx, stepIdx) => {
    setGuides((prev) => {
      const next = [...prev];
      const steps = (next[guideIdx].steps || []).filter((_, i) => i !== stepIdx);
      next[guideIdx] = { ...next[guideIdx], steps };
      return next;
    });
  };

  const uploadStepImage = async (guideIdx, stepIdx, file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/admin/va/config/guide-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        updateStep(guideIdx, stepIdx, {
          imageUrl: res.data.imageUrl,
          displayUrl: res.data.displayUrl
        });
        toast.success('Screenshot uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    }
  };

  const uploadStepVideo = async (guideIdx, stepIdx, file) => {
    const formData = new FormData();
    formData.append('video', file);
    try {
      const res = await api.post('/admin/va/config/guide-videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        updateStep(guideIdx, stepIdx, {
          videoUrl: res.data.videoUrl,
          displayVideoUrl: res.data.displayVideoUrl
        });
        toast.success('Video uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Video upload failed');
    }
  };

  const uploadCategoryImage = async (categoryId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/admin/va/config/guide-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateCategory(categoryId, {
        imageUrl: res.data.imageUrl,
        displayUrl: res.data.displayUrl || null
      });
      toast.success('Category image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    }
  };

  const handleSaveOverview = async () => {
    setSavingOverview(true);
    try {
      await api.put('/admin/va/config', {
        filterRules: overviewConfig.filterRules,
        followersPerAccount: overviewConfig.followersPerAccount
      });
      toast.success('Overview settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSavingOverview(false);
    }
  };

  const handleSave = async () => {
    const invalidGuide = guides.find((g) => !String(g.title || '').trim());
    if (invalidGuide) {
      toast.error('Each guide needs a title');
      return;
    }
    const invalidCategory = categories.find((c) => !String(c.title || '').trim());
    if (invalidCategory) {
      toast.error('Each category needs a title');
      return;
    }

    setSaving(true);
    try {
      const categoryPayload = categories.map((c, i) => ({
        _id: c._id,
        title: String(c.title).trim(),
        imageUrl: c.imageUrl || null,
        order: i
      }));
      const guidePayload = guides.map((g, i) => ({
        ...g,
        order: i,
        title: String(g.title).trim(),
        categoryId: g.categoryId ? String(g.categoryId) : null,
        steps: (g.steps || []).map((s) => ({
          title: s.title || '',
          body: s.body || '',
          imageUrl: s.imageUrl || null,
          videoUrl: s.videoUrl || null,
          _id: s._id
        }))
      }));
      await api.put('/admin/va/config', {
        guides: guidePayload,
        guideCategories: categoryPayload
      });
      toast.success('Guides saved — visible in VA portal');
      loadGuides();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHome = async () => {
    setSavingHome(true);
    try {
      await api.put('/admin/va/config', {
        guidesHome: serializeGuidesHomeForSave(guidesHome)
      });
      toast.success('Guides home page saved');
      loadGuides();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSavingHome(false);
    }
  };

  const renderCategoryColumn = (categoryId, title, entries, categoryMeta = null) => {
    const isOver = dragOverCategoryId === categoryId;
    return (
      <div
        key={categoryId}
        className={`flex flex-col min-h-[220px] rounded-2xl border-2 transition-colors ${
          isOver
            ? 'border-indigo-400 bg-indigo-50/60 dark:bg-indigo-950/20'
            : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOverCategoryId(categoryId);
        }}
        onDragLeave={() => setDragOverCategoryId((prev) => (prev === categoryId ? null : prev))}
        onDrop={(e) => {
          e.preventDefault();
          handleGuideDrop(categoryId);
        }}
      >
        <div className="p-4 border-b border-gray-200/80 dark:border-gray-700/80">
          {categoryMeta ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CategoryIcon category={categoryMeta} />
                <div className="flex-1 min-w-0">
                  <input
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium"
                    value={categoryMeta.title || ''}
                    onChange={(e) => updateCategory(categoryMeta._id, { title: e.target.value })}
                    placeholder="Category name"
                  />
                  <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-indigo-600">
                    <PhotoIcon className="w-3.5 h-3.5" />
                    <span>{categoryMeta.imageUrl ? 'Replace icon/image' : 'Upload icon/image'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadCategoryImage(categoryMeta._id, file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => removeCategory(categoryMeta._id)}
                  className="p-1.5 text-red-500 hover:text-red-700"
                  title="Delete category"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <FolderIcon className="w-5 h-5 text-gray-400" />
              <h4 className="font-medium text-gray-800 dark:text-gray-200">{title}</h4>
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500">{entries.length} guide{entries.length === 1 ? '' : 's'}</p>
        </div>

        <div className="flex-1 p-3 space-y-2">
          {entries.length === 0 && (
            <p className="text-xs text-center text-gray-400 py-6">Drop guides here</p>
          )}
          {entries.map(({ guide, idx }) => {
            const key = guideKey(guide, idx);
            const selected = expandedGuideKey === key;
            return (
              <div
                key={key}
                draggable
                onDragStart={() => setDraggingGuideKey(key)}
                onDragEnd={() => {
                  setDraggingGuideKey(null);
                  setDragOverCategoryId(null);
                }}
                onClick={() => setExpandedGuideKey(key)}
                className={`rounded-xl border px-3 py-2.5 cursor-grab active:cursor-grabbing transition-colors ${
                  selected
                    ? 'border-indigo-400 bg-white dark:bg-gray-800 shadow-sm'
                    : 'border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 hover:border-indigo-300'
                } ${draggingGuideKey === key ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <Bars3Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {guide.title || `Guide ${idx + 1}`}
                    </p>
                    {!guide.published && (
                      <span className="text-[10px] uppercase tracking-wide text-amber-600">Draft</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {categoryMeta && (
            <button
              type="button"
              onClick={() => addGuide(categoryMeta._id)}
              className="w-full mt-1 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
            >
              + Add guide
            </button>
          )}
        </div>
      </div>
    );
  };

  const expandedGuideIdx = guides.findIndex((g, idx) => guideKey(g, idx) === expandedGuideKey);
  const expandedGuide = expandedGuideIdx >= 0 ? guides[expandedGuideIdx] : null;

  if (loading) {
    return <div className="py-12 text-center text-gray-500">Loading guides...</div>;
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">VA portal overview settings</h3>
          <p className="text-sm text-gray-500 mt-1">
            Shown on the VA Overview page (followers target, who to follow, western names guidance).
          </p>
        </div>
        <div>
          <label className="text-sm font-medium">Followers per account (target)</label>
          <input
            type="number"
            min={0}
            className="mt-1 w-40 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
            value={overviewConfig.followersPerAccount || 0}
            onChange={(e) => setOverviewConfig({
              ...overviewConfig,
              followersPerAccount: parseInt(e.target.value, 10) || 0
            })}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Western names (European / English / USA)</label>
          <textarea
            rows={3}
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            value={overviewConfig.filterRules.westernNames}
            onChange={(e) => setOverviewConfig({
              ...overviewConfig,
              filterRules: { ...overviewConfig.filterRules, westernNames: e.target.value }
            })}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Who to follow (filter rules)</label>
          <textarea
            rows={3}
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            value={overviewConfig.filterRules.description}
            onChange={(e) => setOverviewConfig({
              ...overviewConfig,
              filterRules: { ...overviewConfig.filterRules, description: e.target.value }
            })}
          />
        </div>
        <button type="button" onClick={handleSaveOverview} disabled={savingOverview} className="btn-primary disabled:opacity-50">
          {savingOverview ? 'Saving…' : 'Save overview settings'}
        </button>
      </div>

      <VAGuidesHomeEditor
        guidesHome={guidesHome}
        setGuidesHome={setGuidesHome}
        publishedGuides={publishedGuides}
      />
      <div className="flex justify-end -mt-2">
        <button type="button" onClick={handleSaveHome} disabled={savingHome} className="btn-primary disabled:opacity-50">
          {savingHome ? 'Saving…' : 'Save home page'}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <BookOpenIcon className="w-5 h-5" />
            VA Guides
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Organize guides into categories with icons. Drag guides between columns — published guides appear grouped in the VA portal.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={addCategory} className="btn-secondary inline-flex items-center gap-1.5 !py-2 text-sm">
            <PlusIcon className="w-4 h-4" />
            Add category
          </button>
          <button type="button" onClick={() => addGuide(null)} className="btn-secondary inline-flex items-center gap-1.5 !py-2 text-sm">
            <PlusIcon className="w-4 h-4" />
            Add guide
          </button>
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary !py-2 text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Save guides'}
          </button>
        </div>
      </div>

      {guides.length === 0 && categories.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 p-10 text-center">
          <BookOpenIcon className="w-10 h-10 mx-auto text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">No guides yet. Add a category or create your first guide.</p>
        </div>
      )}

      {(categories.length > 0 || guides.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {categories.map((category) => renderCategoryColumn(
            String(category._id),
            category.title,
            guidesByCategory.map.get(String(category._id)) || [],
            category
          ))}
          {renderCategoryColumn(
            UNCATEGORIZED_ID,
            'Uncategorized',
            guidesByCategory.uncategorized
          )}
        </div>
      )}

      {expandedGuide && expandedGuideIdx >= 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40">
            <h4 className="font-medium text-gray-900 dark:text-white">
              Edit: {expandedGuide.title || `Guide ${expandedGuideIdx + 1}`}
            </h4>
            <button
              type="button"
              onClick={() => removeGuide(expandedGuideIdx)}
              className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
            >
              <TrashIcon className="w-4 h-4" />
              Delete guide
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Guide title</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  placeholder="e.g. Profile privacy steps"
                  value={expandedGuide.title || ''}
                  onChange={(e) => updateGuide(expandedGuideIdx, { title: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Short description (optional)</label>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  placeholder="Brief intro shown in the VA portal..."
                  value={expandedGuide.description || ''}
                  onChange={(e) => updateGuide(expandedGuideIdx, { description: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={expandedGuide.published !== false}
                  onChange={(e) => updateGuide(expandedGuideIdx, { published: e.target.checked })}
                  className="rounded"
                />
                Published (visible to VAs)
              </label>
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Steps</label>
                <button type="button" onClick={() => addStep(expandedGuideIdx)} className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                  + Add step
                </button>
              </div>
              {(expandedGuide.steps || []).length === 0 && (
                <p className="text-sm text-gray-500 mb-3">No steps yet. Add text instructions, screenshots, or videos.</p>
              )}
              {(expandedGuide.steps || []).map((step, stepIdx) => (
                <div key={step._id || `step-${stepIdx}`} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Step {stepIdx + 1}</span>
                    <button type="button" onClick={() => removeStep(expandedGuideIdx, stepIdx)} className="text-xs text-red-600 hover:underline">
                      Remove
                    </button>
                  </div>
                  <input
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="Step title"
                    value={step.title || ''}
                    onChange={(e) => updateStep(expandedGuideIdx, stepIdx, { title: e.target.value })}
                  />
                  <textarea
                    rows={3}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="What should the VA do on X.com?"
                    value={step.body || ''}
                    onChange={(e) => updateStep(expandedGuideIdx, stepIdx, { body: e.target.value })}
                  />
                  {step.imageUrl && (
                    <img
                      src={resolveGuideImageUrl(step.imageUrl, step.displayUrl)}
                      alt={step.title || ''}
                      className="max-h-48 rounded-lg border border-gray-200 dark:border-gray-700 object-contain bg-gray-50 dark:bg-gray-900"
                    />
                  )}
                  {step.videoUrl && (
                    <video
                      key={resolveGuideVideoUrl(step.videoUrl, step.displayVideoUrl)}
                      controls
                      playsInline
                      preload="metadata"
                      className="max-h-48 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-black object-contain"
                      src={resolveGuideVideoUrl(step.videoUrl, step.displayVideoUrl)}
                    />
                  )}
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                      <PhotoIcon className="w-4 h-4" />
                      <span>{step.imageUrl ? 'Replace screenshot' : 'Upload screenshot'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadStepImage(expandedGuideIdx, stepIdx, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                      <FilmIcon className="w-4 h-4" />
                      <span>{step.videoUrl ? 'Replace video' : 'Upload video'}</span>
                      <input
                        type="file"
                        accept="video/*"
                        className="sr-only"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadStepVideo(expandedGuideIdx, stepIdx, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <input
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="Video URL (optional — upload above or paste https://...)"
                    value={step.videoUrl || ''}
                    onChange={(e) => updateStep(expandedGuideIdx, stepIdx, { videoUrl: e.target.value || null, displayVideoUrl: null })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VAGuidesSection;
